import React, { useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const TestimonialsSectionImproved = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'center' },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const testimonials = [
    {
      name: 'Jonathan Son',
      rating: 5,
      initial: 'J',
      text: 'Hace una semana llevé mi auto a una empresa dedicada al detailing de vehículos LAVAPOR CAR. Desde el primer momento, el servicio fue personalizado y se notaba su compromiso por brindar un trato especial a cada cliente. Mi vehículo recibió un tratamiento que superó mis expectativas... Sin duda, recomiendo LAVAPOR CAR a cualquiera que busque un servicio de detailing de alta calidad.',
      vehicle: 'BMW Serie 3',
    },
    {
      name: 'Oscar Rea',
      rating: 5,
      initial: 'O',
      text: 'Espectacular Lavapor. Lo principal es la honradez en la asesoría de lo que específicamente necesita mi auto... siempre a tiempo y con el mejor resultado. Muy agradecido siempre chicos.',
      vehicle: 'Mercedes-Benz',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section id="testimonials" className="bg-[#0F1D33] py-24 md:py-32 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#60A5FA] rounded-full filter blur-3xl" />
      </div>

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-7xl mx-auto px-6 relative z-10"
      >
        {/* Section Number */}
        <motion.div variants={itemVariants} className="mb-8">
          <span className="text-[#60A5FA] text-lg font-mono">04</span>
        </motion.div>

        {/* Section Title */}
        <motion.h2 variants={itemVariants} className="text-5xl md:text-6xl font-bold text-white mb-6 text-center">
          ¿Qué tienen que decir <br />
          <span className="text-[#60A5FA]">nuestros clientes?</span>
        </motion.h2>

        <motion.p variants={itemVariants} className="text-white/70 text-lg mb-16 text-center max-w-2xl mx-auto">
          La satisfacción de nuestros clientes es nuestra mejor carta de presentación
        </motion.p>

        {/* Carousel */}
        <motion.div variants={itemVariants} className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="flex-[0_0_100%] md:flex-[0_0_90%] lg:flex-[0_0_80%] px-4">
                  <motion.div
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="relative bg-gradient-to-br from-[#0A1628]/70 to-[#0F1D33]/70 backdrop-blur-2xl border border-white/20 rounded-3xl p-10 md:p-12 overflow-hidden"
                  >
                    {/* Glassmorphism overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
                    
                    {/* Quote Icon */}
                    <div className="absolute top-8 right-8 opacity-10">
                      <Quote className="w-32 h-32 text-[#60A5FA]" />
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      {/* Stars */}
                      <div className="flex space-x-2 mb-6">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-7 h-7 fill-[#60A5FA] text-[#60A5FA]" />
                        ))}
                      </div>

                      {/* Text */}
                      <p className="text-white/90 text-xl leading-relaxed mb-8 font-light italic">
                        "{testimonial.text}"
                      </p>

                      {/* Customer Info */}
                      <div className="flex items-center space-x-4">
                        {/* Avatar */}
                        <div className="w-16 h-16 bg-gradient-to-br from-[#60A5FA] to-[#3B82F6] rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-2xl">
                            {testimonial.initial}
                          </span>
                        </div>
                        
                        <div>
                          <p className="text-white font-bold text-lg">{testimonial.name}</p>
                          <p className="text-white/60 text-sm">{testimonial.vehicle}</p>
                          <p className="text-[#60A5FA] text-xs mt-1">Cliente verificado</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={scrollPrev}
              className="w-14 h-14 bg-[#60A5FA]/20 hover:bg-[#60A5FA]/40 backdrop-blur-sm border border-[#60A5FA]/30 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
            >
              <ChevronLeft className="w-6 h-6 text-[#60A5FA]" />
            </button>
            <button
              onClick={scrollNext}
              className="w-14 h-14 bg-[#60A5FA]/20 hover:bg-[#60A5FA]/40 backdrop-blur-sm border border-[#60A5FA]/30 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
            >
              <ChevronRight className="w-6 h-6 text-[#60A5FA]" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default TestimonialsSectionImproved;
